/**
 * PRODUCT SERIALIZER
 * Transforme les données produits de l'API en format uniforme
 */

/**
 * Sérialiseur pour une seule carte cadeau (avec un montant spécifique)
 */
export function serializeGiftCard(card, amount = null) {
  // Utiliser le montant spécifié ou le premier montant disponible
  const cardAmount = amount || card.metadata?.unitAmounts?.[0] || card.metadata?.minAmount || 0;
  const currency = card.metadata?.currency || 'EUR';
  const rateToXof = card.metadata?.rateToXof || 1;
  const xofPrice = cardAmount * rateToXof;

  return {
    id: amount ? `gift-${card.id}-${amount}` : `gift-${card.id}`,
    title: `${card.name} ${cardAmount}${currency}`,
    name: `${card.name} ${cardAmount}${currency}`,
    amount: xofPrice,
    price: xofPrice,
    unitPrice: xofPrice,
    priceDisplay: `${xofPrice.toLocaleString()} CFA`,
    description: card.description || `Carte cadeau ${card.name}`,
    category: 'giftcard',
    type: 'giftcard',
    image: card.metadata?.image || '',
    url: card.url || '',
    cardValue: cardAmount,
    currency: currency,
    inStock: card.inStock || 0,
    isForBuying: card.metadata?.isForBuying || false,
    raw: card,
    groupName: card.name,
    maxQuantity: 1, // Une seule carte par commande
    useQuantity: false,
    canAddToCart: true,
  };
}

/**
 * Sérialiseur pour les cartes cadeaux - génère un produit par montant disponible
 */
export function serializeGiftCards(cards) {
  if (!Array.isArray(cards)) return [];

  const products = [];

  cards.forEach(card => {
    const amounts = card.metadata?.unitAmounts || [card.metadata?.minAmount];

    // Créer un produit par montant disponible
    if (Array.isArray(amounts) && amounts.length > 0) {
      amounts.forEach(amount => {
        products.push(serializeGiftCard(card, amount));
      });
    } else if (card.metadata?.minAmount) {
      // Fallback: utiliser minAmount si pas d'unitAmounts
      products.push(serializeGiftCard(card, card.metadata.minAmount));
    }
  });

  return products;
}

/**
 * Sérialiseur pour les abonnements
 */
export function serializeSubscription(subscription) {
  const unitAmount = subscription.metadata?.unitAmount || subscription.price || 0;
  const period = subscription.metadata?.period || subscription.period || 'mois';
  const unitPeriod = subscription.metadata?.unitPeriod || subscription.unitPeriod || 'mois';

  return {
    id: `sub-${subscription.id}`,
    title: `Abonnement ${subscription.name}`,
    name: `Abonnement ${subscription.name}`,
    amount: parseInt(unitAmount) || 0,
    price: parseInt(unitAmount) || 0,
    unitPrice: parseInt(unitAmount) || 0,
    priceDisplay: `${parseInt(unitAmount || 0).toLocaleString()} CFA / ${unitPeriod}`,
    period: unitPeriod,
    unitPeriod,
    maxQuantity: 12, // Max 12 mois
    useDuration: true,
    description: subscription.description || `Abonnement ${subscription.name} - Profitez de tous nos contenus`,
    category: 'subscription',
    type: 'subscription',
    image: subscription.image || subscription.metadata?.image || '',
    url: subscription.url || '',
    raw: subscription,
    groupName: subscription.name,
    canAddToCart: true,
  };
}

/**
 * Sérialiseur pour les produits (générique)
 */
export function serializeProduct(product) {
  // Déterminer le type de produit par ownerId (comme dans le code original)
  if (product.ownerId === 'kingrechargeGiftcard') {
    return serializeGiftCard(product);
  }
  if (product.ownerId === 'kingrechargePlatformSubscription') {
    return serializeSubscription(product);
  }
  // Fallback: déterminer le type par type/category
  if (product.type === 'giftcard' || product.category === 'giftCard') {
    return serializeGiftCard(product);
  }
  if (product.type === 'subscription' || product.category === 'subscription') {
    return serializeSubscription(product);
  }

  // Format générique
  return {
    id: product.id,
    title: product.name || product.title,
    name: product.name || product.title,
    amount: product.price || product.amount || 0,
    price: product.price || product.amount || 0,
    unitPrice: product.price || product.amount || 0,
    priceDisplay: `${(product.price || product.amount || 0).toLocaleString()} CFA`,
    description: product.description || '',
    category: product.category || 'other',
    type: product.type || 'product',
    image: product.image || product.metadata?.image || '',
    url: product.url || '',
    raw: product,
    canAddToCart: true,
  };
}

/**
 * Sérialise une liste de produits
 */
export function serializeProducts(products, type = null) {
  // Extraire data si la réponse est un objet avec data
  if (products && typeof products === 'object' && !Array.isArray(products)) {
    products = products.data;
  }

  if (!Array.isArray(products)) return [];

  // Cas spécial pour les cartes cadeaux : générer un produit par montant
  if (type === 'giftcard' || type === 'giftCard') {
    const giftcards = products.filter(p =>
      p.ownerId === 'kingrechargeGiftcard' ||
      p.type === 'giftcard' ||
      p.category === 'giftCard'
    );
    return serializeGiftCards(giftcards);
  }

  // Cas normal : un produit par élément
  return products
    .filter(p => !type || p.type === type || p.category === type || p.service === type || !p.type)
    .map(p => serializeProduct(p));
}

/**
 * Sérialise les options de durée pour un abonnement
 */
export function serializeDurationOptions(basePrice, maxMonths = 12) {
  const options = [
    { value: '1 mois', label: '1 mois', multiplier: 1 },
    { value: '3 mois', label: '3 mois', multiplier: 3 },
    { value: '6 mois', label: '6 mois', multiplier: 6 },
    { value: '12 mois', label: '1 an', multiplier: 12 },
  ];

  return options.map(option => ({
    ...option,
    price: basePrice * option.multiplier,
    priceDisplay: `${(basePrice * option.multiplier).toLocaleString()} CFA`,
  }));
}

export default {
  serializeGiftCard,
  serializeGiftCards,
  serializeSubscription,
  serializeProduct,
  serializeProducts,
  serializeDurationOptions,
};
